import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useBrowseCategories, BrowseCategory } from '@/components/estimates/line-items/hooks/useBrowseCategories';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useNotify } from '@/hooks/useNotify';
import { Trash2, Plus, Edit, Save, X, Lock, ChevronRight, ChevronDown, FolderTree } from 'lucide-react';


interface TreeNode extends BrowseCategory {
  children: TreeNode[];
  isGlobal: boolean;
}

const buildTree = (categories: BrowseCategory[]): TreeNode[] => {
  const map = new Map<string, TreeNode>();
  categories.forEach(c => map.set(c.id, { ...c, children: [], isGlobal: c.company_id === null }));
  const roots: TreeNode[] = [];
  map.forEach(node => {
    if (node.parent_id && map.has(node.parent_id)) {
      map.get(node.parent_id)!.children.push(node);
    } else if (!node.parent_id) {
      roots.push(node);
    }
  });
  const sortNodes = (nodes: TreeNode[]) => {
    nodes.sort((a, b) => a.display_order - b.display_order || a.name.localeCompare(b.name));
    nodes.forEach(n => sortNodes(n.children));
  };
  sortNodes(roots);
  return roots;
};

const flattenForSelect = (nodes: TreeNode[], depth = 0): { id: string; label: string; disabled: boolean }[] => {
  const result: { id: string; label: string; disabled: boolean }[] = [];
  nodes.forEach(n => {
    result.push({ id: n.id, label: '\u00a0'.repeat(depth * 4) + n.name, disabled: false });
    result.push(...flattenForSelect(n.children, depth + 1));
  });
  return result;
};

interface CategoryNodeProps {
  node: TreeNode;
  companyId: string;
  editingId: string | null;
  editingName: string;
  setEditingId: (id: string | null) => void;
  setEditingName: (name: string) => void;
  onSaveEdit: (node: TreeNode) => void;
  onDelete: (node: TreeNode) => void;
  depth?: number;
}

const CategoryNode: React.FC<CategoryNodeProps> = ({
  node, companyId, editingId, editingName,
  setEditingId, setEditingName, onSaveEdit, onDelete, depth = 0,
}) => {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = node.children.length > 0;
  const isEditing = editingId === node.id;

  return (
    <div>
      <div
        className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-muted/50 group"
        style={{ paddingLeft: `${depth * 20 + 8}px` }}
      >
        <button
          type="button"
          className="h-5 w-5 flex items-center justify-center text-muted-foreground"
          onClick={() => hasChildren && setExpanded(e => !e)}
        >
          {hasChildren
            ? expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />
            : <span className="h-4 w-4" />
          }
        </button>

        {isEditing ? (
          <>
            <Input
              value={editingName}
              onChange={e => setEditingName(e.target.value)}
              className="h-7 text-sm flex-1"
              autoFocus
            />
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditingId(null)}>
              <X className="h-4 w-4" />
            </Button>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onSaveEdit(node)}>
              <Save className="h-4 w-4" />
            </Button>
          </>
        ) : (
          <>
            <span className="flex-1 text-sm">{node.name}</span>
            {node.isGlobal && (
              <Badge variant="secondary" className="flex items-center gap-1 text-xs">
                <Lock className="h-3 w-3" />
                Doyence
              </Badge>
            )}
            {!node.isGlobal && (
              <div className="opacity-0 group-hover:opacity-100 flex gap-1 transition-opacity">
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  onClick={() => { setEditingId(node.id); setEditingName(node.name); }}
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  onClick={() => onDelete(node)}
                  disabled={hasChildren}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {hasChildren && expanded && (
        <div>
          {node.children.map(child => (
            <CategoryNode
              key={child.id}
              node={child}
              companyId={companyId}
              editingId={editingId}
              editingName={editingName}
              setEditingId={setEditingId}
              setEditingName={setEditingName}
              onSaveEdit={onSaveEdit}
              onDelete={onDelete}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const BrowseCategoriesSettings = () => {
  const { profile } = useAuth() as any;
  const notify = useNotify();
  const companyId = profile?.company_id as string | undefined;
  const queryClient = useQueryClient();

  const { flat: categories, isLoading: loading } = useBrowseCategories();

  const [newName, setNewName] = useState('');
  const [newParentId, setNewParentId] = useState<string>('__none__');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');


  const addCategory = async () => {
    if (!newName.trim()) {
      notify.warning('Required', 'Category name is required.');
      return;
    }
    if (!companyId) return;

    const parentId = newParentId === '__none__' ? null : newParentId;
    const parent = parentId ? categories.find(c => c.id === parentId) : null;
    const depth = parent ? parent.depth + 1 : 0;

    if (depth > 3) {
      notify.warning('Too deep', 'Categories can only be nested up to 4 levels deep.');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('browse_categories')
        .insert({
          name: newName.trim(),
          parent_id: parentId,
          company_id: companyId,
          display_order: 0,
          depth,
        })
        .select()
        .single();
      if (error) throw error;
      void data;
      queryClient.invalidateQueries({ queryKey: ['browse_categories'] });
      setNewName('');
      setNewParentId('__none__');
      notify.success('Browse category added');
    } catch (err) {
      notify.error(err);
    }
  };

  const saveEdit = async (node: TreeNode) => {
    if (!editingName.trim()) return;
    try {
      const { data, error } = await supabase
        .from('browse_categories')
        .update({ name: editingName.trim() })
        .eq('id', node.id)
        .select()
        .single();
      if (error) throw error;
      void data;
      queryClient.invalidateQueries({ queryKey: ['browse_categories'] });
      setEditingId(null);
      notify.success('Category updated');
    } catch (err) {
      notify.error(err);
    }
  };

  const deleteCategory = async (node: TreeNode) => {
    if (node.children.length > 0) {
      notify.warning('Cannot delete', 'Remove all child categories first.');
      return;
    }
    try {
      const { error } = await supabase
        .from('browse_categories')
        .delete()
        .eq('id', node.id);
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ['browse_categories'] });
      notify.success('Category deleted');
    } catch (err) {
      notify.error(err);
    }
  };

  const tree = buildTree(categories);
  const selectOptions = flattenForSelect(tree);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Browse Categories</CardTitle>
        <CardDescription>
          Add your own browse categories to organise line items in the catalog. Doyence-managed
          categories are shown but cannot be edited.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Add form */}
        <div className="flex flex-col gap-3 sm:grid sm:grid-cols-12 sm:gap-4 sm:items-end">
          <div className="sm:col-span-5 space-y-1">
            <label className="text-sm font-medium">Category name</label>
            <Input
              placeholder="e.g. Plumbing fixtures"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addCategory()}
            />
          </div>
          <div className="sm:col-span-5 space-y-1">
            <label className="text-sm font-medium">Parent category (optional)</label>
            <Select value={newParentId} onValueChange={setNewParentId}>
              <SelectTrigger>
                <SelectValue placeholder="— No parent (top level) —" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— No parent (top level) —</SelectItem>
                {selectOptions.map(opt => (
                  <SelectItem key={opt.id} value={opt.id}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Button onClick={addCategory} className="w-full">
              <Plus className="h-4 w-4 mr-2" />
              Add
            </Button>
          </div>
        </div>

        {/* Tree */}
        <div className="border rounded-md p-2 min-h-[200px]">
          {loading ? (
            <p className="text-center py-8 text-muted-foreground text-sm">Loading...</p>
          ) : tree.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FolderTree className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No browse categories yet.</p>
            </div>
          ) : (
            <div className="space-y-0.5">
              {tree.map(node => (
                <CategoryNode
                  key={node.id}
                  node={node}
                  companyId={companyId ?? ''}
                  editingId={editingId}
                  editingName={editingName}
                  setEditingId={setEditingId}
                  setEditingName={setEditingName}
                  onSaveEdit={saveEdit}
                  onDelete={deleteCategory}
                />
              ))}
            </div>
          )}
        </div>

        <p className="text-xs text-muted-foreground">
          Categories labelled Doyence are managed by Doyence and available to all companies.
          You can add your own categories as children of Doyence categories or as new top-level entries.
          Deleting a category requires removing its children first.
        </p>
      </CardContent>
    </Card>
  );
};

export default BrowseCategoriesSettings;
